import { HStack } from "@design-system/HStack/HStack";
import { Shortcut } from "@design-system/Shortcut/Shortcut";
import { VStack } from "@design-system/VStack/VStack";
import type { BoardKeyboardCopy } from "./boardAnnouncements";
import styles from "./Board.module.scss";

/** The instructions for cards that open on Enter. */
export const getClickableInstructionsId = (instructionsId: string) =>
    `${instructionsId}open`;

interface BoardKeyboardHelpProps {
    /** The cards' `aria-describedby`; clickable ones add `open`. */
    instructionsId: string;
    copy: BoardKeyboardCopy;
    isHintShown: boolean;
}

/**
 * The board's keyboard help: instructions screen readers read with each
 * card, and a quiet hint chip for sighted keyboard users, with a row of the
 * selection keys and card shortcuts when the board has them. The chip stays
 * mounted so it can fade in and out; it repeats the instructions, so
 * screen readers skip it.
 */
export const BoardKeyboardHelp = ({
    instructionsId,
    copy,
    isHintShown,
}: Readonly<BoardKeyboardHelpProps>) => (
    <>
        <div id={getClickableInstructionsId(instructionsId)} hidden>
            {copy.instructions.clickable}
        </div>
        <div id={instructionsId} hidden>
            {copy.instructions.plain}
        </div>
        <VStack
            aria-hidden
            gap={4}
            align="center"
            className={styles.keyboardHint}
            data-visible={isHintShown}
        >
            {copy.hint}
            {copy.hintKeys.length > 0 && (
                <HStack gap={12} wrap justify="center">
                    {copy.hintKeys.map(({ keys, label }) => (
                        <HStack key={keys} gap={4} align="center">
                            <Shortcut keys={keys} size="sm" />
                            {label}
                        </HStack>
                    ))}
                </HStack>
            )}
        </VStack>
    </>
);
